import i18next from "i18next"
import { initReactI18next } from "react-i18next"

import sv from "./i18n/sv/translation.json"
import en from "./i18n/en/translation.json"
import no from "./i18n/no/translation.json"
import da from "./i18n/da/translation.json"
import nl from "./i18n/nl/translation.json"
import de from "./i18n/de/translation.json"
import fr from "./i18n/fr/translation.json"
import es from "./i18n/es/translation.json"
import ptPT from "./i18n/pt-PT/translation.json"
import ptBR from "./i18n/pt-BR/translation.json"
import it from "./i18n/it/translation.json"
import fi from "./i18n/fi/translation.json"
import tr from "./i18n/tr/translation.json"
import uk from "./i18n/uk/translation.json"
import ko from "./i18n/ko/translation.json"
import pl from "./i18n/pl/translation.json"
import zh from "./i18n/zh/translation.json"
import ru from "./i18n/ru/translation.json"

const resources = {
  sv: {
    translation: sv,
  },
  en: {
    translation: en,
  },
  no: {
    translation: no,
  },
  da: {
    translation: da,
  },
  nl: {
    translation: nl,
  },
  de: {
    translation: de,
  },
  fr: {
    translation: fr,
  },
  es: {
    translation: es,
  },
  "pt-PT": {
    translation: ptPT,
  },
  "pt-BR": {
    translation: ptBR,
  },
  it: {
    translation: it,
  },
  fi: {
    translation: fi,
  },
  tr: {
    translation: tr,
  },

  uk: {
    translation: uk,
  },

  ko: {
    translation: ko,
  },

  pl: {
    translation: pl,
  },

  zh: {
    translation: zh,
  },

  ru: {
    translation: ru,
  },
}

// Resolve the language synchronously from the URL path at module-init time.
// On the client this runs before React hydrates, so i18n.language is already
// correct on the very first render and matches the server-rendered markup.
// Without this, the singleton starts on the fallback language (en) and only
// switches asynchronously via changeLanguage(), causing the first hydration
// render of sv/no pages to mismatch the server HTML (React #418/#423/#425).
const getInitialLanguage = () => {
  if (typeof window !== "undefined") {
    const path = window.location.pathname
    if (path === "/sv" || path.startsWith("/sv/")) return "sv"
    if (path === "/no" || path.startsWith("/no/")) return "no"
    if (path === "/da" || path.startsWith("/da/")) return "da"
    if (path === "/nl" || path.startsWith("/nl/")) return "nl"
    if (path === "/de" || path.startsWith("/de/")) return "de"
    if (path === "/fr" || path.startsWith("/fr/")) return "fr"
    if (path === "/es" || path.startsWith("/es/")) return "es"
    if (path === "/pt-pt" || path.startsWith("/pt-pt/")) return "pt-PT"
    if (path === "/pt-br" || path.startsWith("/pt-br/")) return "pt-BR"
    if (path === "/it" || path.startsWith("/it/")) return "it"
    if (path === "/fi" || path.startsWith("/fi/")) return "fi"
    if (path === "/tr" || path.startsWith("/tr/")) return "tr"
    if (path === "/uk" || path.startsWith("/uk/")) return "uk"
    if (path === "/ko" || path.startsWith("/ko/")) return "ko"
    if (path === "/pl" || path.startsWith("/pl/")) return "pl"
    if (path === "/zh" || path.startsWith("/zh/")) return "zh"
  }
  return "en" // Root level is English; SSR sets per-page via changeLanguage
}

export const createI18nInstance = (language = "en") => {
  const instance = i18next.createInstance()
  instance.use(initReactI18next).init({
    resources,
    lng: language,
    fallbackLng: "en",
    debug: false,
    // Inline resources make initialization synchronous. Stating this
    // explicitly is important for Gatsby's server renderer, where every page
    // receives an isolated instance before React renders it.
    initImmediate: false,
    // Keys are full natural-language sentences, so disable separator parsing.
    // Otherwise i18next treats ':' as a namespace separator and '.' as a key
    // separator, silently truncating any key that contains them (e.g. "16:00").
    keySeparator: false,
    nsSeparator: false,
    interpolation: {
      escapeValue: false,
    },
  })
  return instance
}

const i18n = createI18nInstance(getInitialLanguage())

// Function to set language based on URL path
export const setLanguageFromPath = () => {
  if (typeof window !== "undefined") {
    const path = window.location.pathname
    const langMatch = path.match(
      /^\/(en|sv|no|da|fr|es|nl|de|pt-pt|pt-br|it|fi|ru|zh|pl|ko|uk|tr)(?:\/|$)/
    )
    if (langMatch) {
      const lang =
        langMatch[1] === "pt-pt"
          ? "pt-PT"
          : langMatch[1] === "pt-br"
          ? "pt-BR"
          : langMatch[1]
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang)
      }
    }
  }
}

export default i18n
